import React from 'react';
import ImageComponent from '../../atoms/image-component/image-component';
import TextBlock from '../../atoms/text-block/text-block';
import styles from './component-list.module.scss';
import Hero from '../hero/hero';
import Video from '../../atoms/video/video';
import Cta from '../cta/cta';
import CardSet from '../card-set/card-set';
import MediaGallery from '../media-gallery/media-gallery';
import Map from '../map/map';
import AccordionGroup from '../accordion-group/accordion-group';
import Button from '../../../components/atoms/button/button';
import { ButtonStyleType } from '../../../constants/button-style-type';
import IAccordion from '../../../models/accordion/IAccordion';
import IRichText from '../../../models/viewModels/base/IRichText';
import Carousel from '../carousel/carousel';
import ComparisonCarousel from '../comparison-carousel/comparison-carousel';
import colorStyle from '../../../styles/utils/variables.module.scss';
import BaseComponentModel from '../../../models/components/base-component-model';
import RichTextComponentModel from '../../../models/components/rich-text-component-model';
import ImageComponentModel from '../../../models/components/image-component-model';
import VideoComponentModel from '../../../models/components/video-component-model';
import HeroComponentModel from '../../../models/components/hero-component-model';
import CardSetComponentModel from '../../../models/components/card-set-component-model';
import CtaComponentModel from '../../../models/components/cta-component-model';
import MediaGalleryComponentModel from '../../../models/components/media-gallery-component-model';
import MapComponentModel from '../../../models/components/map-component-model';
import AccordionSetComponentModel from '../../../models/components/accordion-set-component-model';
import CarouselComponentModel from '../../../models/components/carousel-component-model';
import ComparisonCarouselComponentModel from '../../../models/components/comparison-carousel-component-model';
import CtaButtonComponentModel from '../../../models/components/cta-button-component-model';
import AdvertBannerComponentModel from '../../../models/components/advert-banner-component-model';
import AdvertBanner from '../advert-banner/advert-banner';
import { checkAdvertBannerContentExists } from '../../../utils/advert-banner/advert-banner-content';
import CtaCardSetComponentModel from '../../../models/components/cta-card-set-component.model';
import CtaHeroComponentModel from '../../../models/components/cta-hero-component-model';
import CtaCardSet from '../cta-card-set/cta-card-set';
import CtaHero from '../cta-hero/cta-hero';
import TestimonialCardSetComponentModel from '../../../models/components/testimonial-card-set-component.model';
import TestimonialCardSet from '../testimonials-card-set/testimonials-card-set';
import SearchBarComponentModel from '../../../models/components/searchbar-component-model';
import SearchBar from '../search-bar/search-bar';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { useTranslations } from 'next-intl';
import { useQueryKeyword } from '../../../hooks/use-query-keyword.hook';
import { ReadonlyURLSearchParams } from 'next/navigation';
import { ApplicationSettings } from '../../../models/settings';
import SpacerComponentModel from '../../../models/components/spacer-component-model';

export type Props = {
  components: BaseComponentModel[];
  isSidebarNavigation?: boolean;
  application?: string;
  router?: AppRouterInstance;
  currentLocale?: string;
  applicationURL?: string;
  searchParams?: ReadonlyURLSearchParams;
  pathname?: string;
};

const unknownComponent: IRichText = {
  json: {
    nodeType: 'document',
    data: {},
    content: [
      {
        nodeType: 'paragraph',
        data: {},
        content: [
          {
            nodeType: 'text',
            value: 'Unknown component',
            data: {},
            marks: [],
          },
        ],
      },
    ],
  },
};

// TODO: Refactoring required. Move to a separate file
// Unify with top-menu.tsx
const searchPath = `/search`;

export type MicrositeSearchBarProps = {
  application: string;
  router: AppRouterInstance;
  currentLocale: string;
  applicationURL: string;
  searchParams: ReadonlyURLSearchParams;
  pathname: string;
  placeholder: string;
  applicationSettings: ApplicationSettings;
  themeColor: string;
};

// Moved search bar here due to conditional React Hooks usage
const MicrositeSearchBar = ({ searchParams, router, currentLocale, applicationURL, pathname, placeholder, applicationSettings, themeColor }: MicrositeSearchBarProps) => {
  const translate = useTranslations(`Common`);
  const queryKeyword = useQueryKeyword(searchParams);

  // TODO: Refactoring required. Move to a separate file
  const isSearchPage = (): boolean => pathname.includes(searchPath);
  const searchHandler = (keyword: string) => {
    if (isSearchPage()) {
      queryKeyword.setKeyword(keyword);
    } else {
      // router.push(`/search?keyword=${keyword}`, { scroll: false });
      // Workaround for router.push acting weird from Page route to App route navigation
      if (typeof window !== 'undefined') {
        const path = `${applicationURL}/${currentLocale}/search?keyword=${keyword}`;
        window.location.href = path;
      }
    }
  };

  return (
    <SearchBar
      idPrefix='microsite-ordered-search'
      onSearch={searchHandler}
      initialKeyword={queryKeyword.getKeyword()}
      cssWrapper='microsite-ordered-search'
      typeAheadPlaceHolder={placeholder}
      router={router}
      translate={translate}
      applicationSettings={applicationSettings}
      currentLocale={currentLocale}
      themeColor={themeColor}
    />
  );
};

export default function ComponentList({
  components,
  isSidebarNavigation,
  application,
  router,
  currentLocale,
  applicationURL,
  searchParams,
  pathname,
}: Props) {
  
  return (
    <div id='component-container' className={`${styles[`component-container`]}`}>
      {components.map((component, index) => {
        if (component.type === 'text') {
          const textComponent = component as RichTextComponentModel;
          return (
            <div key={index} className={isSidebarNavigation ? '' : 'col-xl-9'}>
              <TextBlock text={textComponent.text} id={textComponent.id} />
            </div>
          );
        } else if (component.type === 'image') {
          const imageComponent = component as ImageComponentModel;
          const imageColSize = imageComponent.size === 'normal' ? 'col-xl-9' : '';
          return (
            <div
              key={index}
              className={`${styles[`ordered-page-img`]} ${isSidebarNavigation ? '' : imageColSize}`}
            >
              <ImageComponent
                src={imageComponent.src}
                altText={imageComponent.altText}
                id={imageComponent.id}
              />
            </div>
          );
        } else if (component.type === 'video') {
          const videoOrderedComponent = component as VideoComponentModel;
          return (
            <div key={index} className={isSidebarNavigation ? '' : 'col-xl-9'}>
              <Video
                videoPreload={videoOrderedComponent.videoPreload}
                src={videoOrderedComponent.src}
                key={index}
                poster={videoOrderedComponent.poster}
              />
            </div>
          );
        } else if (component.type === 'hero') {
          const heroOrderedComponent = component as HeroComponentModel;
          return (
            <div key={index} className={isSidebarNavigation ? 'overflow-x-hidden' : ''}>
              <Hero
                key={index}
                src={heroOrderedComponent.src}
                altText={heroOrderedComponent.altText}
                backgroundColor={heroOrderedComponent.backgroundColor}
                id={heroOrderedComponent.id}
              />
            </div>
          );
        } else if (component.type === 'cardSet') {
          const cardSetComponent = component as CardSetComponentModel;
          return (
            <div key={index}>
              <CardSet
                cards={cardSetComponent.cardList}
                appSettings={cardSetComponent.appSettings}
                id={cardSetComponent.id}
              />
            </div>
          );
        } else if (component.type === 'cta') {
          const ctaOrderedComponent = component as CtaComponentModel;
          return (
            <Cta
              key={index}
              ctaID={ctaOrderedComponent.ctaID}
              textColor={ctaOrderedComponent.textColor}
              wrapperBorderColor={ctaOrderedComponent.wrapperBorderColor}
              wrapperBackgroundColor={ctaOrderedComponent.wrapperBackgroundColor}
              url={ctaOrderedComponent.url}
              text={ctaOrderedComponent.text}
              buttons={ctaOrderedComponent.buttons}
              appSettings={ctaOrderedComponent.appSettings}
            />
          );
        } else if (component.type === 'mediaGallery') {
          const mediaGalleryComponent = component as MediaGalleryComponentModel;
          if (mediaGalleryComponent?.gallery?.length) {
            return (
              <div key={index} className={isSidebarNavigation ? '' : 'col-xl-9'}>
                <MediaGallery
                  galleryItems={mediaGalleryComponent.gallery}
                  hash={typeof window !== 'undefined' ? window.location?.hash : null}
                />
              </div>
            );
          } else {
            return null;
          }
        } else if (component.type === 'map') {
          const mapComponent = component as MapComponentModel;
          return (
            <div key={index}>
              <Map
                points={mapComponent.points}
                zoomLevel={mapComponent.zoomLevel}
                fallbackImage={mapComponent.fallbackImage}
                apiKey={mapComponent.googleApiKey}
                id={mapComponent.id}
              />
            </div>
          );
        } else if (component.type === 'accordionSet') {
          const accordionSetComponent = component as AccordionSetComponentModel;
          const accordionGroup = accordionSetComponent.accordionSet?.map((x) => {
            return {
              id: x.id,
              heading: x.heading,
              heapIdentifier: x.heapIdentifier,
              content: <TextBlock text={x.content.text} />,
            } as IAccordion;
          });
          return (
            <AccordionGroup
              key={index}
              accordionProps={accordionGroup}
              id={typeof window !== 'undefined' ? window.location?.hash : null}
            />
          );
        } else if (component.type === 'ctaButton') {
          const ctaButtonComponent = component as CtaButtonComponentModel;
          const isButtonOutlineStyled =
            ctaButtonComponent.ctaButtonFields.buttonStyle === ButtonStyleType.Outline;

          return (
            <Button
              key={index}
              buttonID={ctaButtonComponent.ctaButtonFields.buttonID}
              buttonStyle={ctaButtonComponent.ctaButtonFields.buttonStyle}
              buttonNewTab={ctaButtonComponent.ctaButtonFields.buttonNewTab}
              buttonURL={ctaButtonComponent.ctaButtonFields.buttonURL}
              buttonAriaLabel={ctaButtonComponent.ctaButtonFields.buttonAriaLabel}
              buttonSize={ctaButtonComponent.ctaButtonFields.buttonSize}
              buttonName={ctaButtonComponent.ctaButtonFields.buttonName}
              copyColor={
                isButtonOutlineStyled
                  ? ctaButtonComponent.appSettings.branding.primaryColor
                  : colorStyle.white
              }
              backgroundColor={
                isButtonOutlineStyled
                  ? colorStyle.white
                  : ctaButtonComponent.appSettings.branding.primaryColor
              }
              outlineColor={ctaButtonComponent.appSettings.branding.primaryColor}
              hoverBackgroundColor={ctaButtonComponent.appSettings.branding.primaryHoverColor}
              activeBackgroundColor={ctaButtonComponent.appSettings.branding.primaryActiveColor}
              heapIdentifier={ctaButtonComponent.ctaButtonFields.heapIdentifier}
            >
              {ctaButtonComponent?.ctaButtonFields.children}
            </Button>
          );
        } else if (component.type === 'carousel') {
          const carouselComponent = component as CarouselComponentModel;
          return (
            <div
              key={index}
              className={isSidebarNavigation ? 'overflow-x-hidden default-carousel' : ''}
            >
              <Carousel
                carouselItems={carouselComponent.carousel}
                enableBorder={carouselComponent.enableBorder}
                aspectRatio={carouselComponent.aspectRatio}
                imageSize={carouselComponent.imageSize}
                id={carouselComponent.id}
                isSidebarNavigation={isSidebarNavigation}
              />
            </div>
          );
        } else if (component.type === 'comparisonCarousel') {
          const comparisonComponent = component as ComparisonCarouselComponentModel;
          return (
            <div key={index} className={isSidebarNavigation ? 'overflow-x-hidden' : ''}>
              <ComparisonCarousel
                image={comparisonComponent.image}
                carousel={comparisonComponent.carousel}
                id={comparisonComponent.id}
              />
            </div>
          );
        } else if (component.type === 'advertBanner') {
          const advertBannerComponent = component as AdvertBannerComponentModel;
          // check if advert banner data exist
          const checkAdvertExists = checkAdvertBannerContentExists(advertBannerComponent);

          if (!checkAdvertExists) {
            return null;
          }

          return (
            <div key={index} className={isSidebarNavigation ? '' : 'col-xl-9'}>
              <AdvertBanner
                advertBanner={advertBannerComponent}
                appBranding={advertBannerComponent.appSettings.branding}
                application={application}
              />
            </div>
          );
        } else if (component.type === 'ctaCardSet') {
          const ctaCardSetComponent = component as CtaCardSetComponentModel;
          return (
            <div key={index}>
              <CtaCardSet
                ctaCards={ctaCardSetComponent.cards}
                appSettings={ctaCardSetComponent.appSettings}
                id={ctaCardSetComponent.id}
              />
            </div>
          );
        } else if (component.type === 'ctaHero') {
          const ctaHeroComponent = component as CtaHeroComponentModel;
          return (
            <div key={index} className={isSidebarNavigation ? 'overflow-x-hidden' : ''}>
              <CtaHero
                ctaHero={ctaHeroComponent.ctaHero}
                appSettings={ctaHeroComponent.appSettings}
              />
            </div>
          );
        } else if (component.type === 'testimonialCardSet') {
          const testimonialCardSetComponent = component as TestimonialCardSetComponentModel;
          return (
            <div key={index} className= {`${styles[`testimonial-heading`]} default-carousel`}>
              <h2>{testimonialCardSetComponent.title}</h2>
              <TestimonialCardSet
                testimonialCards={testimonialCardSetComponent.cards}
                id={testimonialCardSetComponent.id}
              />
            </div>
          );
        } else if (component.type === 'searchBar' && application === 'UniBrandedB2c') {
          const searchBarComponent = component as SearchBarComponentModel;
          return (
            <div key={index} className={isSidebarNavigation ? '' : 'col-xl-8'}>
              <MicrositeSearchBar
                applicationSettings={searchBarComponent.appSettings}
                currentLocale={currentLocale}
                themeColor={searchBarComponent.appSettings.branding.primaryColor}
                application={application}
                router={router}
                applicationURL={applicationURL}
                searchParams={searchParams}
                pathname={pathname}
                placeholder={searchBarComponent.placeholder}
              />
            </div>
          );
        } else if (component.type === 'spacer') {
          const spacerComponent = component as SpacerComponentModel;
          return <div key={index} style={{ height: `${spacerComponent.spacerSize}px` }} className={`${styles[`spacer`]}`}/>;
        } else if (component.type === 'unknown') {
          return <TextBlock key={index} text={unknownComponent} />;
        }
        return null;
      })}
    </div>
  );
}
